/**
 * useSubscription - Subscription status composable
 *
 * Provider-agnostic subscription management with localStorage caching.
 * Pattern follows useBeltProgress:
 * - localStorage is primary for instant UX
 * - Background fetch from API for fresh data
 * - 5-minute cache TTL
 *
 * The app only interacts with this composable, never with LemonSqueezy directly.
 * This allows easy provider swap (to Stripe, etc.) by only changing API endpoints.
 */

import { ref, computed, inject, type Ref, type ComputedRef } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { resolveSupabase } from './schools/client'
import { bootFetch } from '../playback/signedInBoot'
import { CHECKOUT_COMPLETED_EVENT } from '../lib/checkoutEvents'
import type {
  Subscription,
  SubscriptionStatus,
  SubscriptionResponse,
  PortalResponse,
  OrgFreeAccess,
} from '../types/Subscription'

// ============================================================================
// STORAGE KEYS
// ============================================================================

export { CHECKOUT_COMPLETED_EVENT as CHECKOUT_COMPLETED_EVENT_NAME }

const SUBSCRIPTION_KEY = 'ssi_subscription'
// NO TTL ON THE MIRROR (Tom, 2026-09-12, job #378·G). The localStorage copy of
// the last /api/subscription answer used to expire after five minutes, on the
// theory that a fresh fetch always follows. Offline, no fetch follows: a
// paying Family owner reopened the app in airplane mode two hours after his
// last online boot, the mirror was thrown away for being old, the fetch failed
// at once, and initialize() declared hydration done with nothing — every
// premium course dropped to the free preview and Settings offered him a plan
// he already pays for. The mirror is the ONLY answer the app has offline, so
// its age is never a reason to discard it: an online answer overwrites it the
// moment one lands, `clearCache` drops it on sign-out and on a 401, and
// `isSubscribed` still checks the paid period's end date against the clock,
// so a period that has genuinely ended fails closed however old the copy is.

// Bounds the "hasHydrated stays false" grace period on the initial fetch.
// isPending (derived from !hasHydrated in useEntitlement) optimistically
// unlocks premium UI while a legitimate fetch is in flight — necessary
// because a page-load race shouldn't bounce a paying subscriber to the
// paywall. But that same optimism fails OPEN forever if /api/subscription
// is simply never allowed to resolve (blocked request, dead connection).
// After this timeout we declare hydration done (fail closed — not
// subscribed) regardless; the in-flight fetch keeps running in the
// background and will correct `subscription` the moment it actually
// resolves. Real content enforcement is server-side (checkCourseAccess
// here is UI-only), so this only bounds how long the cosmetic grace
// period can be held open.
const HYDRATION_TIMEOUT_MS = 8000

// ============================================================================
// TYPES
// ============================================================================

interface CachedSubscription {
  subscription: Subscription | null
  isSubscribed: boolean
  /** Free-through-a-funded-org grant, cached with the same TTL as the
   *  subscription so a reload doesn't flash an upgrade prompt at a learner
   *  whose year is paid for. */
  freeAccess?: OrgFreeAccess | null
  cachedAt: number
  /** The auth uid this answer was given for (job #895): read back only for
   *  that same person, so the last account's plan never shows on the next. */
  ownerId?: string | null
}

export interface UseSubscriptionReturn {
  /** Current subscription (null if none) */
  subscription: Ref<Subscription | null>
  /** Whether user has active subscription */
  isSubscribed: ComputedRef<boolean>
  /** Whether we're loading subscription data */
  isLoading: Ref<boolean>
  /** True once the first fetch attempt (or a no-auth short-circuit) has
   *  completed — before this, `isSubscribed` is not yet a trustworthy
   *  "not subscribed" signal. */
  hasHydrated: Ref<boolean>
  /** Error message if any */
  error: Ref<string | null>
  /** Subscription status for display */
  status: ComputedRef<SubscriptionStatus>
  /** Initialize — call from App.vue after supabase + auth are ready */
  initialize: () => Promise<void>
  /** Open customer portal */
  openPortal: () => Promise<void>
  /** Cancel the subscription at period end (returns the end date if known) */
  cancelSubscription: () => Promise<{ ok: boolean; effectiveAt?: string | null; error?: string }>
  /** Refresh subscription from API */
  refresh: () => Promise<void>
  /** The identity changed: forget any answer not given for `userId`, void
   *  every ask in flight, mark the answer not yet in, and ask again (bounded
   *  like the boot hydration). */
  switchIdentity: (userId: string | null) => Promise<void>
  /** Same person, something changed server-side: void asks in flight, mark
   *  the answer not yet in, and ask again (bounded). */
  revalidate: () => Promise<void>
  /** Re-read until the API reports an active subscription (or timeout). */
  pollUntilActive: (timeoutMs?: number) => Promise<void>
  /** Clear local cache */
  clearCache: () => void
  /** True when this learner IS a parent-minted child account — never offered a
   *  checkout (job #376·F, D7). */
  isChildAccount: Ref<boolean>
  /** The funded-org grant paying for this learner's access, if any. */
  freeAccess: Ref<OrgFreeAccess | null>
  /** NOBODY WHOSE ACCESS IS ALREADY FREE IS EVER SHOWN A PRICE.
   *  True while a funded org enrolment (Canolfan's free year) is still
   *  running. Every upgrade prompt is suppressed by this one signal, so a
   *  new surface asks the same question as the old ones instead of inventing
   *  its own idea of "free". */
  hasFreeAccess: ComputedRef<boolean>
  /** True when the SERVER says this learner is an ssi_admin. Suppresses every
   *  upgrade affordance: a platform admin outranks Premium and is never sold
   *  anything (Tom, 2026-09-08). Read from /api/subscription, which resolves it
   *  from the learner row — deliberately NOT from useUserRole, whose cache is
   *  localStorage and is writable by the browser. */
  isPlatformAdmin: Ref<boolean>
}

// A RENEWING PAYER OFFLINE STAYS A PAYER ACROSS THE ROLLOVER (job #549).
// Tom's rulings: 2026-07-10 "definitely do NOT favour security over paying
// user experience"; 2026-09-12, job #378, "a payer offline stays a payer".
// `currentPeriodEnd` is only the end of the CURRENT period. For a subscription
// set to renew, Paddle extends it by webhook at the rollover and the app learns
// of the new end only from the next successful /api/subscription answer. A
// device offline across that instant — a flight, a holiday, a train — still
// holds the old end in its mirror, so without a grace the learner drops to the
// free preview and is offered a plan they already pay for, until they next get
// online. So a renewing subscription is treated as paid for this long past
// its recorded period end. Seven days covers Paddle's dunning/retry window
// and any realistic offline stretch; a real cancellation or a failed payment
// reaches the device as a status change on the next online refresh, which
// overwrites the mirror and ends the grace at once. A subscription with
// `cancelAtPeriodEnd` set, or any status other than 'active', ends exactly at
// `currentPeriodEnd`, as job #540 has it — there is no renewal to wait for.
// UI-only: the server-side content gate is unchanged by this constant.
export const RENEWAL_GRACE_MS = 7 * 24 * 60 * 60 * 1000

// How often the entitlement clock re-checks the paid period's end while the
// app is simply open. Coarse on purpose: the resume-shaped events below catch
// a device waking from sleep at once, so this only bounds how long a period
// end can go unnoticed on a screen that never sleeps or changes tab.
const CLOCK_TICK_MS = 60 * 1000

/**
 * Wire the reactive clock to the moments a stale "still paid" answer would
 * otherwise survive: a coarse interval for a screen left open, and the
 * resume-shaped events (tab shown, window focused, bfcache restore, network
 * back) for a device that slept through the period end. Lives for the life of
 * the page — the composable is a module singleton, not a component — so there
 * is nothing to tear down. No-op outside a browser (SSR, node tests).
 */
function installClockTicks(tick: () => void): void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  setInterval(tick, CLOCK_TICK_MS)
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', tick)
  }
  window.addEventListener('focus', tick)
  window.addEventListener('pageshow', tick)
  window.addEventListener('online', tick)
}

// ============================================================================
// COMPOSABLE
// ============================================================================

export function useSubscription(): UseSubscriptionReturn {
  // Supabase client for auth token
  const supabaseRef = inject<{ value: SupabaseClient | null }>('supabase')

  // Subscription state
  const subscription = ref<Subscription | null>(null)
  const isLoading = ref(false)
  const hasHydrated = ref(false)
  const error = ref<string | null>(null)
  // THE GENERATION (job #895): every ask captures it, every identity change
  // and revalidation bumps it, and an answer from a superseded ask writes
  // nothing — not the plan, not the mirror, not "hydrated". Account A's late
  // reply cannot land on account B.
  let generation = 0
  // Whose answer the in-memory state and the mirror are. Undefined = a mirror
  // written before job #895 (no owner): trusted for the boot as before.
  let heldFor: string | null | undefined = null
  // A parent-minted child account: no email of their own, no way to pay, so
  // no checkout is ever opened for them (job #376·F, D7).
  const isChildAccount = ref(false)
  // Free through a funded org enrolment (Canolfan's free year) — see
  // api/_utils/orgFreeAccess.ts. Drives prompt suppression, never access:
  // the courses the grant unlocks are carried by user_entitlements, which
  // checkCourseAccess already honours.
  const freeAccess = ref<OrgFreeAccess | null>(null)
  // Server-decided; see the interface note. Defaults false, so a failed or
  // unauthenticated fetch never quietly grants anybody the admin treatment.
  const isPlatformAdmin = ref(false)

  // THE CLOCK IS A DEPENDENCY (Astra refutation of job #378, confirmed
  // 2026-09-13). `isSubscribed` and `hasFreeAccess` compare an end date against
  // "now", but a Vue computed re-runs only when a REACTIVE dependency changes
  // and `new Date()` is not one. So a period that read "ends in 30 minutes" at
  // boot stayed `true` for as long as `subscription.value` was left alone —
  // and offline it is left alone forever, because the refresh fetch fails and
  // never rewrites the ref. A lapsed payer kept paid content across
  // navigation, resume and every timer tick until the next full reload.
  // `clock` is bumped on a coarse interval and on every resume-shaped event,
  // the two computeds read it, and the comparison itself uses Date.now() at
  // evaluation so the answer is exact whenever it is recomputed.
  const clock = ref(Date.now())
  const tickClock = () => { clock.value = Date.now() }
  installClockTicks(tickClock)

  // Computed
  const isSubscribed = computed(() => {
    void clock.value
    if (!subscription.value) return false
    if (subscription.value.status !== 'active') return false

    // Check if within the paid period. A subscription that is set to renew
    // gets RENEWAL_GRACE_MS past its recorded end (see the constant); one that
    // is ending, or a mirror written by the cancel path, ends exactly there.
    if (subscription.value.currentPeriodEnd) {
      const periodEnd = new Date(subscription.value.currentPeriodEnd).getTime()
      if (!Number.isNaN(periodEnd)) {
        const grace = subscription.value.cancelAtPeriodEnd ? 0 : RENEWAL_GRACE_MS
        if (periodEnd + grace < Date.now()) return false
      }
    }

    return true
  })

  // No renewal grace here: a funded-org grant is a FIXED-TERM gift
  // (org_enrolments.free_access_until — "their year", api/_utils/orgFreeAccess.ts),
  // nothing renews it, so its end is its end.
  const hasFreeAccess = computed(() => {
    void clock.value
    const grant = freeAccess.value
    // until === null is a permanently free enrolment (free_forever): no end,
    // so never expired. A missing/blank date on anything else stays "no grant".
    if (grant && grant.until === null) return true
    const until = grant?.until
    if (!until) return false
    const ends = new Date(until)
    return !Number.isNaN(ends.getTime()) && ends.getTime() > Date.now()
  })

  const status = computed((): SubscriptionStatus => {
    return subscription.value?.status ?? 'none'
  })

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  function loadFromCache(): CachedSubscription | null {
    try {
      const cached = localStorage.getItem(SUBSCRIPTION_KEY)
      if (!cached) return null

      const data: CachedSubscription = JSON.parse(cached)
      if (!data || typeof data !== 'object' || !('subscription' in data)) return null
      // Any age is accepted — see the note on SUBSCRIPTION_KEY. The fetch that
      // initialize() starts is what refreshes it; this copy only has to hold
      // the line until that answer arrives, or for the whole session if none does.
      return data
    } catch {
      return null
    }
  }

  function saveToCache(sub: Subscription | null, subscribed: boolean, grant: OrgFreeAccess | null, ownerId: string | null): void {
    try {
      const data: CachedSubscription = {
        subscription: sub,
        isSubscribed: subscribed,
        freeAccess: grant,
        cachedAt: Date.now(),
        ownerId,
      }
      localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(data))
    } catch {
      // Ignore storage errors
    }
  }

  function clearCache(): void {
    // Reset in-memory state too, not just the persisted copy — otherwise the
    // previous user's subscription lingers in the ref after sign-out (useAuth
    // calls this on logout) until a reload. isSubscribed is computed from
    // `subscription`, so it follows automatically.
    generation++
    heldFor = null
    subscription.value = null
    freeAccess.value = null
    try {
      localStorage.removeItem(SUBSCRIPTION_KEY)
    } catch {
      // Ignore
    }
  }

  // ============================================================================
  // API CALLS
  // ============================================================================

  async function getAuthToken(): Promise<string | null> {
    return (await getSession())?.token ?? null
  }

  async function getSession(): Promise<{ token: string; userId: string | null } | null> {
    const client = resolveSupabase(supabaseRef)
    if (!client) return null

    try {
      const { data: { session } } = await client.auth.getSession()
      return session?.access_token ? { token: session.access_token, userId: session.user?.id ?? null } : null
    } catch {
      return null
    }
  }

  /**
   * One read of /api/subscription. Through the signed-in boot by default: a
   * standing boot answers for its token within its TTL. `fresh` goes to the
   * network regardless (job #222): the poll after a Paddle redirect is asking
   * whether the activating webhook has landed SINCE the page loaded, and the
   * boot, made before it, can only repeat its old "no" — pinned at 16 s before
   * the first real read, with the learner just paid and staring at the paywall.
   * The boot itself is left standing for its other readers.
   */
  async function fetchSubscription(fresh = false): Promise<void> {
    const gen = generation
    const current = () => gen === generation
    try {
      const session = await getSession()
      if (!current()) return
      const token = session?.token
      if (!token) {
        // Not authenticated - clear subscription. This IS a resolved state
        // (guest), so hydration is complete.
        subscription.value = null
        isChildAccount.value = false
        freeAccess.value = null
        isPlatformAdmin.value = false
        hasHydrated.value = true
        return
      }

      const init = { headers: { 'Authorization': `Bearer ${token}` } }
      const response = await (fresh ? fetch('/api/subscription', init) : bootFetch('/api/subscription', init))
      if (!current()) return

      if (!response.ok) {
        if (response.status === 401) {
          // Auth issue - clear cache and state
          clearCache()
          generation = gen // clearCache voids asks in flight; this one is still the answer
          subscription.value = null
          freeAccess.value = null
          hasHydrated.value = true
          return
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data: SubscriptionResponse = await response.json()
      if (!current()) return
      // The server could not read store purchases (plan 035), so its "no" is not an
      // answer. Treat it as a failed ask: a store buyer is never downgraded on a blip.
      // Only isPlatformAdmin is safe to take from such an answer: it comes from the learner
      // row, whose failure is a 503. isChildAccount and freeAccess come from reads that are
      // fail-soft too (family_members, and user_entitlements for covered courses), so in a
      // marked answer they may be defaults, not facts; the held values stand.
      if (data.storeGrantUnknown && !data.isSubscribed) {
        isPlatformAdmin.value = !!data.isPlatformAdmin
        throw new Error('store purchases unreadable')
      }

      heldFor = session?.userId ?? null
      subscription.value = data.subscription
      isChildAccount.value = !!data.isChildAccount
      freeAccess.value = data.freeAccess ?? null
      isPlatformAdmin.value = !!data.isPlatformAdmin
      saveToCache(data.subscription, data.isSubscribed, freeAccess.value, heldFor ?? null)
      hasHydrated.value = true
    } catch (err) {
      if (!current()) return
      console.error('[useSubscription] Fetch error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to fetch subscription'
      // A network/API failure leaves hydration incomplete on purpose — we
      // don't know the real status, so don't downgrade a possibly-active
      // subscriber to "unsubscribed" (isPending stays true; the gate keeps
      // optimistically allowing access until a real answer arrives).
    }
  }

  async function refresh(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      await fetchSubscription()
    } finally {
      isLoading.value = false
    }
  }

  // ============================================================================
  // PORTAL
  // ============================================================================

  async function openPortal(): Promise<void> {
    const token = await getAuthToken()
    if (!token) {
      error.value = 'Please sign in'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch('/api/subscription/portal', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to open portal')
      }

      const data: PortalResponse = await response.json()

      // Open portal in a new tab. The fetch above outlives the click gesture,
      // so iOS Safari may block the popup (returns null) — fall back to a
      // same-tab navigation so the button never silently does nothing.
      const win = window.open(data.portalUrl, '_blank')
      if (!win) window.location.href = data.portalUrl
    } catch (err) {
      console.error('[useSubscription] Portal error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to open portal'
    } finally {
      isLoading.value = false
    }
  }

  async function cancelSubscription(): Promise<{ ok: boolean; effectiveAt?: string | null; error?: string }> {
    const token = await getAuthToken()
    if (!token) return { ok: false, error: 'Please sign in' }

    isLoading.value = true
    error.value = null
    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const msg = data?.error || 'Failed to cancel subscription'
        error.value = msg
        return { ok: false, error: msg }
      }
      // Optimistically reflect the scheduled cancellation, then refresh from the
      // API so the webhook-confirmed state wins once it lands.
      if (subscription.value) {
        subscription.value = { ...subscription.value, cancelAtPeriodEnd: true }
      }
      await fetchSubscription()
      return { ok: true, effectiveAt: data?.effectiveAt ?? null }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel subscription'
      error.value = msg
      return { ok: false, error: msg }
    } finally {
      isLoading.value = false
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Load from cache immediately
  const cached = loadFromCache()
  if (cached) {
    subscription.value = cached.subscription
    freeAccess.value = cached.freeAccess ?? null
    heldFor = cached.ownerId
  }

  /**
   * Poll the subscription API until it reports active (or timeout). Used right
   * after a Paddle checkout redirect, where the activating webhook may lag the
   * page load by a few seconds — without this the just-subscribed learner would
   * briefly re-hit the paywall. Money capture is unchanged; this only re-reads
   * the already-written subscription state.
   *
   * The bound is the whole poll, reads included (job #222). It used to be
   * checked only between reads, and a read has no deadline of its own, so one
   * hung read held the "30 second" poll for ever — and initialize awaits this
   * on ?just_subscribed=1. Now each read, and the sleep after it, is cut to
   * the time left: a hung read ends with the poll at 30 s, and slow reads no
   * longer overrun it. A read abandoned at the bound is left to settle on its
   * own; its answer, if it comes, is a real one and may land.
   */
  async function pollUntilActive(timeoutMs = 30000): Promise<void> {
    const start = Date.now()
    const left = () => timeoutMs - (Date.now() - start)
    while (left() > 0) {
      let bound: ReturnType<typeof setTimeout> | undefined
      try {
        await Promise.race([
          fetchSubscription(true),
          new Promise<void>((resolve) => { bound = setTimeout(resolve, left()) }),
        ])
      } finally {
        clearTimeout(bound)
      }
      if (isSubscribed.value) return
      if (left() <= 0) return
      await new Promise((r) => setTimeout(r, Math.min(2000, left())))
    }
  }

  /** One ask, its "not yet in" bounded at HYDRATION_TIMEOUT_MS — and the
   *  bound belongs to THIS ask: a newer ask (identity change) owns the flag. */
  async function hydrateBounded(): Promise<void> {
    const gen = generation
    let deadline: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        fetchSubscription(),
        new Promise<void>((resolve) => { deadline = setTimeout(resolve, HYDRATION_TIMEOUT_MS) }),
      ])
    } finally {
      clearTimeout(deadline)
    }
    // Timed out, or the fetch failed outright (offline) — declare hydration
    // done rather than leave the answer pending indefinitely. This fails
    // CLOSED only for an identity that has never held an answer: one that
    // has keeps the mirror loaded above, so a payer offline stays a payer.
    if (gen === generation && !hasHydrated.value) hasHydrated.value = true
  }

  async function switchIdentity(userId: string | null): Promise<void> {
    generation++
    hasHydrated.value = false
    if (!userId || heldFor !== userId) {
      subscription.value = null
      freeAccess.value = null
      isChildAccount.value = false
      isPlatformAdmin.value = false
      heldFor = userId
      try { localStorage.removeItem(SUBSCRIPTION_KEY) } catch { /* ignore */ }
    }
    await hydrateBounded()
  }

  async function revalidate(): Promise<void> {
    generation++
    hasHydrated.value = false
    await hydrateBounded()
  }

  async function initialize(): Promise<void> {
    if (!resolveSupabase(supabaseRef)) return
    await hydrateBounded()
    // Just came back from Paddle checkout — keep polling until the activating
    // webhook lands so the learner doesn't bounce off the paywall mid-redirect.
    try {
      const justSubscribed = new URLSearchParams(window.location.search).get('just_subscribed') === '1'
      if (justSubscribed && !isSubscribed.value) await pollUntilActive()
    } catch { /* URL parse is best-effort */ }
  }

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    isChildAccount,
    freeAccess,
    hasFreeAccess,
    isPlatformAdmin,
    subscription,
    isSubscribed,
    isLoading,
    hasHydrated,
    error,
    status,
    initialize,
    pollUntilActive,
    openPortal,
    cancelSubscription,
    refresh,
    switchIdentity,
    revalidate,
    clearCache,
  }
}

// ============================================================================
// SHARED INSTANCE
// ============================================================================

let sharedInstance: ReturnType<typeof useSubscription> | null = null

export function useSharedSubscription(): UseSubscriptionReturn {
  if (!sharedInstance) {
    sharedInstance = useSubscription()
    listenForCheckoutCompletion(sharedInstance)
  }
  return sharedInstance
}

/**
 * CONVERGE ON THE PURCHASE WITHOUT THE REDIRECT.
 *
 * The success redirect (?just_subscribed=1) is the only thing that used to tell
 * this app somebody had paid, and it is not ours to guarantee: a standalone PWA,
 * a blocked top-level navigation, an Apple Pay sheet that returns in place, or a
 * buyer who taps away from the receipt all skip it. When it is skipped the app
 * keeps its pre-purchase state — subscription null, Settings offering the
 * Upgrade row — while the money is gone and the webhook has already written the
 * row. That is what a brand-new account buying SSi Family hit on 2026-09-07.
 *
 * Paddle fires checkout.completed in-page regardless. Here we take that as the
 * cue and poll the same API the boot path polls, so the app catches up within
 * seconds of the webhook landing, redirect or no redirect. Idempotent: polling
 * stops the moment the subscription reads active, and a checkout that DID
 * redirect simply finds it active on the first read.
 */
function listenForCheckoutCompletion(instance: UseSubscriptionReturn): void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  window.addEventListener(CHECKOUT_COMPLETED_EVENT, () => {
    // Drop the cached pre-purchase answer first: the 5-minute TTL would
    // otherwise hand a stale "not subscribed" to the next page load.
    try {
      localStorage.removeItem(SUBSCRIPTION_KEY)
    } catch {
      // Storage unavailable — the poll below is what actually matters.
    }
    void instance.pollUntilActive()
  })
}
